import { ObjectType } from '@fullstory/proto/fs/services/dx-objects';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import { useFlags } from '@fullstory/react-hooks';
import { LibraryObjectType } from '@fullstory/routes';
import { useMemo } from 'react';
import { useRetentionEnabled } from './useRetentionEnabled';
import { useDashboardCreateAccess, fullstorySelectors } from '@fullstory/redux';
import { useSelector } from 'react-redux';

const { getOrgId } = fullstorySelectors;

export type LibraryObjectAccess = {
  /**
   * Is this object type included in the org's plan?
   */
  included: boolean;
  /**
   * Is this object type disabled for the specific user (i.e. create disabled)?
   */
  disabled?: boolean;
  /**
   * Can this object type be displayed in cards?
   */
  supportsCards?: boolean;
  /**
   * Can this object type be displayed and filtered in the library table views?
   */
  supportsLibrary?: boolean;
};

export type LibraryAccessMap = {
  [type in LibraryObjectType]: LibraryObjectAccess;
};

const defaultObjectAccess: LibraryObjectAccess = {
  disabled: false,
  included: true,
  supportsCards: true,
  supportsLibrary: true,
};

/**
 * Returns a map of access to all library object types.
 */
export const useLibraryObjectTypesAccess = (): LibraryAccessMap => {
  const flags = useFlags();
  const retentionEnabled = useRetentionEnabled();
  const orgId = useSelector(getOrgId);

  const canCreateDashboards = useDashboardCreateAccess(
    orgId,
    flags,
    flags.hasBit(Flag.FeatDashboards),
  );

  const objectTypes = useMemo(
    (): LibraryAccessMap => ({
      [ObjectType.DASHBOARD]: {
        ...defaultObjectAccess,
        included: flags.hasBit(Flag.FeatDashboards),
        supportsCards: true,
        disabled: !canCreateDashboards,
      },
      [ObjectType.SEGMENT]: defaultObjectAccess,
      [ObjectType.METRIC]: defaultObjectAccess,
      [ObjectType.FUNNEL]: defaultObjectAccess,
      [ObjectType.RETENTION]: { ...defaultObjectAccess, included: retentionEnabled },
      [ObjectType.JOURNEY]: {
        ...defaultObjectAccess,
        included: flags.hasBit(Flag.FeatJourneys),
      },
      [ObjectType.HEATMAP]: defaultObjectAccess,
      [ObjectType.DATA_TABLE]: {
        ...defaultObjectAccess,
        included: flags.featureFlags().AppDataTables,
        supportsCards: flags.featureFlags().AppDataTablesCards,
        supportsLibrary: flags.featureFlags().AppDataTables,
      },
    }),
    [retentionEnabled, flags, canCreateDashboards],
  );

  return objectTypes;
};
